import React from 'react';
import { Lock, Mail, X, FileText, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../utils/formatCurrency';

// ============================================
// Modal: Confirmar oferta
// ============================================
export const ConfirmBidModal = ({
  bidAmount,
  confirmAmount,
  setConfirmAmount,
  handleConfirmBid,
  onClose,
  selectedConditions = [],
  availableConditions = [],
  conditionsText = ''
}) => {
  const { t, i18n } = useTranslation();
  const selectedConditionObjects = availableConditions.filter(c =>
    selectedConditions.includes(c.id)
  );

  const hasConditions = selectedConditionObjects.length > 0 || conditionsText.trim();

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-slate-800 rounded-2xl max-w-md w-full border border-slate-700" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">{t('confirm.title')}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-all">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {/* Cantidad */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-center">
            <div className="text-slate-400 text-sm mb-2">{t('confirm.youEntered')}</div>
            <div className="text-3xl font-bold text-amber-400">{formatCurrency(parseFloat(bidAmount), i18n.language)}</div>
          </div>

          {/* Mostrar condiciones si existen */}
          {hasConditions && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-semibold text-blue-400">{t('confirm.conditionsTitle')}</span>
              </div>

              {/* Condiciones predefinidas */}
              {selectedConditionObjects.length > 0 && (
                <div className="space-y-2 mb-3">
                  {selectedConditionObjects.map(condition => (
                    <div key={condition.id} className="flex items-center gap-2 text-sm text-slate-300">
                      <span className="text-base">{condition.icon}</span>
                      <span>{condition.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Texto libre */}
              {conditionsText.trim() && (
                <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                  <div className="text-xs text-slate-400 mb-1">{t('confirm.additionalConditions')}</div>
                  <div className="text-sm text-slate-200">{conditionsText}</div>
                </div>
              )}
            </div>
          )}

          {/* Confirmación de cantidad */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {t('confirm.reenterLabel')}
            </label>
            <div className="relative">
              <input
                type="number"
                required
                value={confirmAmount}
                onChange={(e) => setConfirmAmount(e.target.value)}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg pl-4 pr-8 py-3 text-white text-xl font-semibold focus:outline-none focus:border-amber-500"
                placeholder={t('confirm.amountPlaceholder')}
                autoFocus
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">€</span>
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 bg-slate-700 text-white font-medium py-3 rounded-lg hover:bg-slate-600 transition-all"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleConfirmBid}
              className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold py-3 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all"
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
